import React, { useState, useRef, useEffect } from 'react';
import { useCurrency, CURRENCIES } from '../../context/CurrencyContext';
import { ChevronDown } from 'lucide-react';

/**
 * Currency selector — matches the LanguageToggle aesthetic.
 * variant: 'dark' (default, for nav utility bar) | 'light'
 */
export default function CurrencySelector({ variant = 'dark', className = '' }) {
  const { currency, setCurrency, supported, meta } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const isLight = variant === 'light';
  const btnBase = isLight
    ? 'border-[#E8E4DC] hover:border-[#D4AF37] bg-white hover:bg-[#FAF7F0] text-[#1A2F4A]'
    : 'border-white/25 hover:border-[#D4AF37]/70 bg-white/5 hover:bg-[#D4AF37]/10 text-white';

  return (
    <div ref={ref} className={`relative z-[60] inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Currency selector, current currency ${currency}`}
        data-testid="currency-selector"
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60 ${btnBase}`}>
        <span aria-hidden="true">{meta.flag}</span>
        <span className={isLight ? 'text-[#1A2F4A]' : 'text-[#D4AF37]'}>{currency}</span>
        <ChevronDown className={`w-3 h-3 ${isLight ? 'text-[#1A2F4A]/60' : 'text-white/60'} transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Currency"
          className="absolute right-0 mt-2 w-44 bg-white border border-[#E8E4DC] rounded-xl shadow-lg py-1 z-[70] overflow-hidden"
          data-testid="currency-menu">
          {supported.map((code) => {
            const c = CURRENCIES[code];
            const active = code === currency;
            return (
              <li key={code} role="none">
                <button
                  role="option"
                  aria-selected={active}
                  onClick={() => { setCurrency(code); setOpen(false); }}
                  data-testid={`currency-opt-${code}`}
                  className={`w-full text-left px-3 py-2 text-[12px] flex items-center gap-2 transition-colors ${
                    active ? 'bg-[#FAF7F0] text-[#1A2F4A] font-bold' : 'text-[#1A2F4A] hover:bg-[#FAFAF8]'
                  }`}>
                  <span aria-hidden="true">{c.flag}</span>
                  <span className="font-semibold">{code}</span>
                  <span className="text-[#6B4E37] text-[11px] ml-auto">{c.symbol}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
